import { useEffect, useRef, useMemo, memo } from "react";
import { Chart } from "@antv/g2";
import { uniqueId } from "@antv/util";
import { theme } from "antd";
import { isEqual } from "lodash";

const AUTO_PALETTE = [
  "#1677FF",
  "#389E0D",
  "#D46B08",
  "#531DAB",
  "#C41D7F",
  "#D4B106",
  "#0958D9",
  "#237804",
  "#AD4E00",
  "#391085",
];

// Converts device-mode wide format [{ date, device, error, warning }]
// → long format [{ date, device, level, count, isTop }]
function toLong(data) {
  const hasError = new Set(
    data.filter((d) => d.error > 0).map((d) => `${d.date}||${d.device}`),
  );
  const rows = [];
  data.forEach(({ date, device, warning, error }) => {
    const key = `${date}||${device}`;
    if (warning > 0)
      rows.push({
        date,
        device,
        level: "Warning",
        count: warning,
        isTop: !hasError.has(key),
      });
    if (error > 0)
      rows.push({ date, device, level: "Error", count: error, isTop: true });
  });
  return rows;
}

/**
 * Two modes:
 *
 * 1. Device mode (default, colorField omitted):
 *    data = [{ date, device, error, warning }]
 *    → grouped stacked bar (devices side-by-side, Warning/Error stacked)
 *
 * 2. Generic mode (colorField provided):
 *    data = [{ date, [colorField], [yField] }]   e.g. { date, alarm, count }
 *    → simple stacked bar grouped by colorField value
 *    colorDomain controls which values appear and their order in the legend/stack.
 *    colorRange is optional; defaults to AUTO_PALETTE.
 */
function CustomGroupedStackedBarChart({
  data = [],
  // Generic-mode props — omit for device mode
  colorField = null,
  yField = "count",
  xField = "date",
  colorDomain: colorDomainProp = [],
  colorRange: colorRangeProp = [],
  // When true: generic mode uses dodgeX (grouped) instead of stackY
  grouped = false,
  // Shared props
  xAxisTitle = "Date",
  yAxisTitle = "Count",
  height = 320,
  onBarClick = null,
}) {
  const { token } = theme.useToken();
  const id = useRef(uniqueId("grouped-stacked-bar-"));
  const intervalRef = useRef(null);
  const chartRef = useRef(null);
  const prevDataRef = useRef([]);
  const onBarClickRef = useRef(onBarClick);
  useEffect(() => {
    onBarClickRef.current = onBarClick;
  }, [onBarClick]);

  const isDeviceMode = colorField === null;

  // Prepare processed data + resolved color scale
  const { processedData, resolvedDomain, resolvedRange } = useMemo(() => {
    if (isDeviceMode) {
      return {
        processedData: toLong(data),
        resolvedDomain: ["Error", "Warning"],
        resolvedRange: [token.colorError, token.colorWarning],
      };
    }
    const domain =
      colorDomainProp.length > 0
        ? colorDomainProp
        : [...new Set(data.map((d) => d[colorField]))];
    const range =
      colorRangeProp.length > 0
        ? colorRangeProp
        : domain.length <= AUTO_PALETTE.length
          ? domain.map((_, i) => AUTO_PALETTE[i])
          : domain.map(
              (_, i) =>
                `hsl(${Math.round((i * 360) / domain.length)}, 65%, 50%)`,
            );
    return {
      processedData: data,
      resolvedDomain: domain,
      resolvedRange: range,
    };
  }, [data, isDeviceMode, colorField, colorDomainProp, colorRangeProp, token]);

  useEffect(() => {
    const containerId = id.current;
    const container = document.getElementById(containerId);
    if (!container) return;
    container.innerHTML = "";

    const chart = new Chart({ container: containerId, autoFit: true, height });

    chart.scale("color", {
      domain: resolvedDomain,
      range: resolvedRange,
    });

    const interval = chart
      .interval()
      .data(processedData)
      .encode("x", isDeviceMode ? "date" : xField)
      .encode("y", isDeviceMode ? "count" : yField)
      .encode("color", isDeviceMode ? "level" : colorField)
      .style("stroke", "none");

    if (isDeviceMode) {
      // Grouped stacked: devices side-by-side, Warning/Error stacked per device
      interval
        .encode("series", "device")
        .transform({ type: "stackY", groupBy: ["x", "series"] })
        .transform({ type: "dodgeX", groupBy: "series" })
        .style("fill", (d) => {
          if (d.level === "Warning") return token.colorWarning;
          if (d.level === "Error") return token.colorError;
          return "transparent"; // phantom series bars — hide them
        })
        // Encode device||level so tooltip render can parse both fields
        .tooltip((d) => ({
          name: `${d.device || ""}||${d.level || ""}`,
          value: d.count ?? 0,
          color: d.level === "Warning" ? token.colorWarning : token.colorError,
        }))
        .label({
          text: (d) => (d.isTop ? d.device : ""),
          position: "top",
          fill: token.colorTextBase,
          fontSize: 10,
          textAnchor: "start",
          dy: -4,
          style: {
            transform: "rotate(-90)",
          },
        });

      chart.interaction("tooltip", {
        render: (event, { title, items }) => {
          const normalizedTitle = Array.isArray(title) ? title[0] : title;
          const byDevice = {};
          items.forEach((item) => {
            const [device, level] = (item.name || "").split("||");
            if (!device || !level) return;
            if (!byDevice[device]) byDevice[device] = {};
            byDevice[device][level] = item.value;
          });
          const dot = (color) =>
            `<span style="width:8px;height:8px;background:${color};border-radius:50%;display:inline-block;margin-right:4px;flex-shrink:0;"></span>`;
          const rows = Object.entries(byDevice)
            .map(
              ([device, levels]) => `
              <li style="margin:5px 0;">
                <div style="font-weight:600;margin-bottom:3px;">${device}</div>
                <div style="display:flex;flex-direction:column;gap:2px;padding-left:8px;font-size:12px;">
                  ${levels.Error !== undefined ? `<span style="display:flex;align-items:center;">${dot(token.colorError)}Error: <strong style="margin-left:4px;">${levels.Error}</strong></span>` : ""}
                  ${levels.Warning !== undefined ? `<span style="display:flex;align-items:center;">${dot(token.colorWarning)}Warning: <strong style="margin-left:4px;">${levels.Warning}</strong></span>` : ""}
                </div>
              </li>`,
            )
            .join("");
          return `<div style="min-width:190px;">
            <h3 style="padding:0;margin:0 0 6px">${normalizedTitle}</h3>
            <ul style="padding:0;margin:0;list-style:none;">${rows || "<li style='opacity:0.5'>No data</li>"}</ul>
          </div>`;
        },
      });
    } else if (grouped) {
      // Grouped: one bar per colorField value side-by-side, no stacking
      interval
        .encode("series", colorField)
        .transform({ type: "dodgeX", groupBy: "series" })
        .tooltip((d) => ({
          name: String(d[colorField] ?? "Unknown"),
          value: d[yField] ?? 0,
        }));

      chart.interaction("tooltip", {
        render: (event, { title, items }) => {
          const normalizedTitle = Array.isArray(title) ? title[0] : title;
          const validItems = items.filter(
            (d) => d.name && d.value !== undefined,
          );
          return `<div style="min-width:160px;">
            <h3 style="padding:0;margin:0 0 6px">${normalizedTitle}</h3>
            <ul style="padding:0;margin:0;list-style:none;">
              ${validItems
                .map(
                  (d) => `
                <li style="display:flex;justify-content:space-between;align-items:center;margin:3px 0;">
                  <span style="display:inline-flex;align-items:center;">
                    <span style="width:8px;height:8px;background:${d.color};border-radius:50%;margin-right:8px;flex-shrink:0;"></span>
                    <span>${d.name}</span>
                  </span>
                  <strong style="margin-left:16px;">${d.value}</strong>
                </li>`,
                )
                .join("")}
            </ul>
          </div>`;
        },
      });
    } else {
      // Simple stacked: each colorField value is a stacked segment
      interval.transform({ type: "stackY" }).tooltip((d) => ({
        name: String(d[colorField] ?? "Unknown"),
        value: d[yField] ?? 0,
      }));

      chart.interaction("tooltip", {
        render: (event, { title, items }) => {
          const normalizedTitle = Array.isArray(title) ? title[0] : title;
          const validItems = items.filter(
            (d) => d.name && d.name !== yField && d.value !== undefined,
          );
          return `<div style="min-width:160px;">
            <h3 style="padding:0;margin:0 0 6px">${normalizedTitle}</h3>
            <ul style="padding:0;margin:0;list-style:none;">
              ${validItems
                .map(
                  (d) => `
                <li style="display:flex;justify-content:space-between;align-items:center;margin:3px 0;">
                  <span style="display:inline-flex;align-items:center;">
                    <span style="width:8px;height:8px;background:${d.color};border-radius:50%;margin-right:8px;flex-shrink:0;"></span>
                    <span>${d.name}</span>
                  </span>
                  <strong style="margin-left:16px;">${d.value}</strong>
                </li>`,
                )
                .join("")}
            </ul>
          </div>`;
        },
      });
    }

    chart.axis("x", {
      line: true,
      lineStroke: token.colorTextBase,
      tickStroke: token.colorTextBase,
      title: xAxisTitle,
      titleFill: token.colorTextBase,
      labelFill: token.colorTextBase,
    });

    chart.axis("y", {
      line: true,
      lineStroke: token.colorTextBase,
      tickStroke: token.colorTextBase,
      title: yAxisTitle,
      titleFill: token.colorTextBase,
      labelFill: token.colorTextBase,
    });

    chart.legend({
      color: { itemLabelFill: token.colorTextBase },
    });

    chart.on("interval:click", (evt) => {
      const datum = evt.data?.data;
      if (datum) onBarClickRef.current?.(datum);
    });

    chart.render();
    chartRef.current = chart;
    intervalRef.current = interval;
    prevDataRef.current = processedData;

    return () => {
      chart.destroy();
    };
  }, [
    token,
    xAxisTitle,
    yAxisTitle,
    height,
    isDeviceMode,
    grouped,
    colorField,
    yField,
    xField,
    resolvedDomain,
    resolvedRange,
  ]);

  useEffect(() => {
    if (!intervalRef.current) return;
    if (!isEqual(prevDataRef.current, processedData)) {
      intervalRef.current.changeData(processedData);
      prevDataRef.current = processedData;
    }
  }, [processedData]);

  return <div id={id.current} style={{ width: "100%", height }} />;
}

export default memo(CustomGroupedStackedBarChart, (prev, next) => {
  const { onBarClick: _a, ...prevProps } = prev;
  const { onBarClick: _b, ...nextProps } = next;
  return isEqual(prevProps, nextProps);
});
